import React from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";

import LoadingOverlay from "@auto/widgets/LoadingOverlay";
import { PRINT_EVENT } from "@auto/events/Gui";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./VoterRoll.module.styl";

const POLLING_STATION_SIZE = 350;
const VOTER_FETCH_PAGE = 1000;
const PERSON_FETCH_BATCH = 400;

const quoteRsqlValue = (value) => {
  if (typeof value === "number") {
    return String(value);
  }

  return `"${String(value).replaceAll('"', '\\"')}"`;
};

const rsqlIn = (selector, values) =>
  `${selector}=in=(${values.map(quoteRsqlValue).join(",")})`;

const chunks = (values, size) => {
  const out = [];

  for (let i = 0; i < values.length; i += size) {
    out.push(values.slice(i, i + size));
  }

  return out;
};

const canonicalText = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .trim()
    .toUpperCase();

const compareText = (a, b) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const canonicalDate = (value) =>
  value ? String(value).slice(0, 10) : "";

const compareEntries = (a, b) => {
  return (
    compareText(canonicalText(a.lastName), canonicalText(b.lastName)) ||
    compareText(canonicalText(a.firstName), canonicalText(b.firstName)) ||
    compareText(canonicalDate(a.dateOfBirth), canonicalDate(b.dateOfBirth)) ||
    compareText(canonicalText(a.nationalIdentifier), canonicalText(b.nationalIdentifier))
  );
};

const splitIntoPollingStations = (entries) => {
  if (!entries.length) {
    return [];
  }

  const stationCount = Math.ceil(entries.length / POLLING_STATION_SIZE);
  const baseSize = Math.floor(entries.length / stationCount);
  const remainder = entries.length % stationCount;
  const stations = [];
  let offset = 0;

  for (let i = 0; i < stationCount; i++) {
    const stationSize = baseSize + (i < remainder ? 1 : 0);
    stations.push(entries.slice(offset, offset + stationSize));
    offset += stationSize;
  }

  return stations;
};

const photoUrl = (entry) => {
  const id = Number(entry?.defaultImageId);

  return Number.isFinite(id) && id > 0
    ? `${getServiceUri()}person-photo/data/${id}`
    : "";
};

const loadAllVoters = async (votingCenterGid) => {
  const query = `votingCenterGid==${Number(votingCenterGid)}`;

  const total = Number(
    await rest.request(
      getServiceUri() + "voter/count",
      "POST",
      { columns: [], query, limit: null, offset: null }
    ) || 0
  );

  const voters = [];

  for (let offset = 0; offset < total; offset += VOTER_FETCH_PAGE) {
    const page = await rest.request(
      getServiceUri() + "voter/search",
      "POST",
      {
        columns: [],
        query,
        limit: Math.min(VOTER_FETCH_PAGE, total - offset),
        offset
      }
    );

    voters.push(...(page || []));
  }

  return voters;
};

const loadPeople = async (voters) => {
  const personGids = Array.from(
    new Set(
      (voters || [])
        .map((voter) => Number(voter?.personGid))
        .filter((gid) => Number.isFinite(gid))
    )
  );

  const people = [];

  for (const batch of chunks(personGids, PERSON_FETCH_BATCH)) {
    const rows = await rest.request(
      getServiceUri() + "person/search",
      "POST",
      {
        columns: [],
        query: rsqlIn("gid", batch),
        limit: batch.length,
        offset: 0
      }
    );

    people.push(...(rows || []));
  }

  return new Map(
    people.map((person) => [Number(person.gid), person])
  );
};

const loadPreviewEntries = async (votingCenterGid) => {
  const voters = await loadAllVoters(votingCenterGid);
  const peopleByGid = await loadPeople(voters);

  return voters
    .map((voter) => {
      const person = peopleByGid.get(Number(voter.personGid));

      return {
        voterGid: voter.gid,
        personGid: voter.personGid,
        votingCenterGid: voter.votingCenterGid,
        nationalIdentifier: person?.nationalIdentifier || "",
        firstName: person?.firstName || "",
        lastName: person?.lastName || "",
        dateOfBirth: person?.dateOfBirth || null,
        sexCode: person?.sexCode || "",
        defaultImageId: person?.defaultImageId || null
      };
    })
    .sort(compareEntries);
};

const PollingStationBooklet = ({ center, stationNumber, entries, showPhotos }) => (
  <article className={styles.booklet}>
    <header className={styles.bookletHeader}>
      <div className={styles.bookletAgency}>{t("voterRoll.agencyName")}</div>
      <h1>{t("voterRoll.bookletTitle")}</h1>
      <div className={styles.bookletMeta}>
        <div><strong>{t("voter.votingCenter")}:</strong> {center?.code ? `${center.code} · ` : ""}{center?.name || ""}</div>
        <div><strong>{t("voterRoll.pollingStation")}:</strong> {stationNumber}</div>
        {center?.address ? <div><strong>{t("common.address")}:</strong> {center.address}</div> : null}
        <div><strong>{t("voterRoll.entries")}:</strong> {entries.length}</div>
        <div><strong>{t("common.type")}:</strong> {t("voterRoll.livePreviewType")}</div>
      </div>
    </header>

    <table className={styles.bookletTable}>
      <thead>
        <tr>
          <th>{t("voterRoll.number")}</th>
          <th>{t("voterRoll.photo")}</th>
          <th>{t("voter.ninu")}</th>
          <th>{t("voterRoll.familyName")}</th>
          <th>{t("voterRoll.firstName")}</th>
          <th>{t("voterRoll.dateOfBirth")}</th>
          <th>{t("voterRoll.sex")}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => (
          <tr key={`${entry.voterGid}-${index}`}>
            <td>{index + 1}</td>
            <td>
              {showPhotos && photoUrl(entry) ? (
                <img
                  className={styles.bookletPhoto}
                  src={photoUrl(entry)}
                  alt=""
                />
              ) : null}
            </td>
            <td>{entry.nationalIdentifier}</td>
            <td>{entry.lastName}</td>
            <td>{entry.firstName}</td>
            <td>{entry.dateOfBirth ? String(entry.dateOfBirth).slice(0, 10) : ""}</td>
            <td>{entry.sexCode}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </article>
);

const PRINT_STYLE = `
@page {
  size: A4 portrait;
  margin: 10mm 8mm 10mm 8mm;
}

@media print {
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
`;

const VoterRollPreview = ({ gui, gid }) => {
  const [center, setCenter] = React.useState(null);
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [printState, setPrintState] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const [report, liveEntries] = await Promise.all([
          rest.request(
            getServiceUri() + `reporting/voting-center-reports/${Number(gid)}`,
            "GET"
          ),
          loadPreviewEntries(Number(gid))
        ]);

        if (!cancelled) {
          setCenter(report || null);
          setEntries(liveEntries || []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [gid]);

  if (loading) {
    return <div className={styles.loading}>{t("voterRoll.loadingPreview")}</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const stations = splitIntoPollingStations(entries);

  const printStation = (stationNumber, stationEntries) => {
    setPrintState({ phase: "PREPARING" });

    PRINT_EVENT.publish({
      content: (
        <PollingStationBooklet
          center={center}
          stationNumber={stationNumber}
          entries={stationEntries}
          showPhotos={can(gui, "READ_PERSON_PHOTO")}
        />
      ),
      style: PRINT_STYLE,
      copyParentStyle: true,
      onPrintDialogRequested: () => {
        setPrintState({ phase: "DIALOG_REQUESTED" });
      },
      onAfterPrint: () => {
        setPrintState(null);
      }
    });
  };

  const printMessage = printState?.phase === "DIALOG_REQUESTED"
    ? t("voterRoll.openingPrintDialog")
    : t("voterRoll.preparingBooklet");

  return (
    <LoadingOverlay active={Boolean(printState)} spinner text={printMessage}>
      <div className={styles.page}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => gui.goTo("/reporting/voting-centers")}
      >
        <ArrowBackIcon fontSize="small" />
        {t("reporting.votingCenterReports")}
      </button>

      <header className={styles.hero}>
        <div>
          <div className={styles.eyebrow}>{t("voterRoll.livePreview")}</div>
          <h1 className={styles.title}>{center?.name || t("voter.votingCenter")}</h1>
          <div className={styles.subtitle}>
            {[center?.code, center?.communeName, center?.departmentName]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>

        <div className={styles.previewTotals}>
          <strong>{entries.length.toLocaleString()}</strong>
          <span>{t("voterRoll.registeredVoters")}</span>
          <strong>{stations.length.toLocaleString()}</strong>
          <span>{t("voterRoll.pollingStations")}</span>
        </div>
      </header>

      <div className={styles.previewNotice}>
        {t("voterRoll.previewLiveNotice")}
      </div>

      <section className={styles.stationGrid}>
        {stations.map((stationEntries, index) => {
          const number = index + 1;

          return (
            <article className={styles.stationCard} key={number}>
              <div>
                <div className={styles.stationNumber}>
                  {t("voterRoll.pollingStation")} {number}
                </div>
                <div className={styles.secondaryText}>
                  {stationEntries.length.toLocaleString()} {t("voterRoll.votersLower")}
                </div>
              </div>

              <button
                type="button"
                className={styles.printButton}
                disabled={Boolean(printState)}
                onClick={() => printStation(number, stationEntries)}
              >
                <PrintIcon fontSize="small" />
                {t("voterRoll.printBooklet")}
              </button>
            </article>
          );
        })}
      </section>
      </div>
    </LoadingOverlay>
  );
};

export const displayVoterRollPreview = (gui, gid) => ({
  uuid: `voter-roll-preview-${gid}`,
  view: () => <VoterRollPreview gui={gui} gid={gid} />
});

export default VoterRollPreview;
